import React from 'react';
import { connect } from 'react-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import { getTranslate, getActiveLanguage } from 'react-localize-redux';
import routes from 'library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import UserHelper from 'infrastructure/UserHelper';

// Material UI imports
import AppBar from '@material-ui/core/AppBar';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import { IconButton, Button } from '@material-ui/core';
import Toolbar from '@material-ui/core/Toolbar';
import CloseIcon from '@material-ui/icons/Close';
import Slide from '@material-ui/core/Slide';
import { withStyles } from '@material-ui/core/styles';
import {
  Link,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';

// Form handling
import { withFormik, FormikProps } from 'formik';

// Components
import { Recaptcha } from '../../components/Shared/Recaptcha';

// Interfaces
interface TransitionProps {
  children?: React.ReactElement;
}

interface PendingClientFieldProps {
  labelResourceName?: string;
  name: string;
  label: string;
  children: React.ReactNode;
}

interface ContactStudioFormProps {
  translate: (key: string) => string;
  classes: Record<string, string>;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  studio: {
    _id: string;
    owner_id: string;
  };
  user: any;
  domain: {
    domain: {
      id: string;
    };
  };
}

interface FormValues {
  domain: string;
  anonymous: boolean;
  studioId: string;
  ownerId: string;
  rank?: string;
  content?: string;
  canSubmit?: boolean;
}

interface StateProps {
  translate: (key: string) => string;
  currentLanguage: string;
  user: any;
  myStudios: any;
  domain: {
    domain: {
      id: string;
    };
  };
}

interface ContactStudioFormState {}

type Props = ContactStudioFormProps & FormikProps<FormValues>;

const Transition = React.forwardRef<any, TransitionProps>((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

class ContactStudioForm extends React.Component<Props, ContactStudioFormState> {
  PendingClientField = ({ labelResourceName, name, label, children }: PendingClientFieldProps) => {
    const { classes, errors, translate } = this.props;
    const error = errors[name];
    return (
      <FormControl className={classes.field} error={!!error}>
        <InputLabel className={classes.label}>
          {label}
          {error && <FormHelperText>&nbsp;{error}</FormHelperText>}
        </InputLabel>
        {children}
      </FormControl>
    );
  };

  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  onPendingReviewFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.handleChange('rank')(e.target.value);
  };

  render() {
    const { PendingClientField } = this;
    const { values, handleChange, translate, classes, open, onClose, user, isSubmitting, dirty, errors, handleSubmit } =
      this.props;

    return (
      <Dialog
        fullScreen={LayoutHelper.isMobile()}
        open={open}
        onClose={onClose}
        aria-labelledby={translate('pendingReview.addReviewTitle')}
        TransitionComponent={Transition}
      >
        <AppBar className={classes.appBar}>
          <Toolbar>
            <IconButton color="inherit" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" color="inherit" className={classes.flex}>
              {translate('pendingReview.addReviewTitle')}
            </Typography>
          </Toolbar>
        </AppBar>
        <Typography component="div" className={classes.mainTypography}>
          {UserHelper.isLoggedIn(user) ? (
            <form onSubmit={handleSubmit}>
              <PendingClientField label={translate('pendingReview.rank')} name="rank">
                <Typography align="left">
                  <div className={classes.studioRate}>
                    <input
                      required
                      id="rate1-star5"
                      type="radio"
                      name="rank"
                      value={5}
                      onChange={this.onPendingReviewFieldChange}
                    />
                    <label htmlFor="rate1-star5" title="Excellent">
                      <StarIcon />
                    </label>

                    <input
                      required
                      id="rate1-star4"
                      type="radio"
                      name="rank"
                      value={4}
                      onChange={this.onPendingReviewFieldChange}
                    />
                    <label htmlFor="rate1-star4" title="Good">
                      <StarIcon />
                    </label>

                    <input
                      required
                      id="rate1-star3"
                      type="radio"
                      name="rank"
                      value={3}
                      onChange={this.onPendingReviewFieldChange}
                    />
                    <label htmlFor="rate1-star3" title="Satisfactory">
                      <StarIcon />
                    </label>

                    <input
                      required
                      id="rate1-star2"
                      type="radio"
                      name="rank"
                      value={2}
                      onChange={this.onPendingReviewFieldChange}
                    />
                    <label htmlFor="rate1-star2" title="Bad">
                      <StarIcon />
                    </label>

                    <input
                      required
                      id="rate1-star1"
                      type="radio"
                      name="rank"
                      value={1}
                      onChange={this.onPendingReviewFieldChange}
                    />
                    <label htmlFor="rate1-star1" title="Very bad">
                      <StarIcon />
                    </label>
                  </div>
                </Typography>
              </PendingClientField>

              <PendingClientField label={translate('pendingReview.publicFeedback')} name="content">
                <TextField
                  id="content"
                  multiline
                  variant="outlined"
                  value={values.content || ''}
                  onChange={handleChange}
                />
              </PendingClientField>
              <FormControlLabel
                name="anonymous"
                control={<Checkbox checked={values.anonymous} onChange={handleChange} />}
                label={translate('pendingReview.anonymous')}
              />
              <PendingClientField name="canSubmit" label={translate('common.captcha')}>
                <div style={{ marginBottom: '10px' }}>
                  <Recaptcha onVerify={(canSubmit: boolean) => handleChange('canSubmit')(canSubmit)} />
                </div>
              </PendingClientField>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting || !dirty || Object.keys(errors).length !== 0}
              >
                {isSubmitting ? LayoutHelper.renderLoader() : ''}
                {translate('studio.submitReview')}
              </Button>
            </form>
          ) : (
            <Typography variant="body2">
              {translate('studio.reviewCTA')}{' '}
              <Link href={`${routes.clientLogin()}?path=${window.location.pathname}`}>
                {translate('studio.promocodeSignIn')}
              </Link>
            </Typography>
          )}
        </Typography>
      </Dialog>
    );
  }
}

function mapStateToProps(state: any): StateProps {
  const translate = getTranslate(state.locale);
  const currentLanguage = getActiveLanguage(state.locale).code;
  const { user, myStudios, domain } = state;
  return { translate, currentLanguage, user, myStudios, domain };
}

const styles = (theme: any) => ({
  appBar: {
    position: 'relative',
  },
  grid: {
    width: '60%',
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
  mainTypography: { margin: theme.spacing(2) },
  bigAvatar: {
    margin: 10,
    width: 100,
    height: 100,
  },
  paperActivity: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  root: {
    flexGrow: 1,
  },
  tabs: {
    flexGrow: 1,
  },
  onHoldTab: {
    padding: 8 * 3,
  },
  fab: {
    position: 'fixed',
    right: theme.spacing(1),
    bottom: theme.spacing(1),
    zIndex: 10000,
  },
  subTeacherInfo: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  teacherInfo: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  unavailableNotification: {
    border: '1px solid #d0d0d2',
    background: 'rgba(212,88,63, 0.1)',
  },
  actions: {
    display: 'flex',
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  paper: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    margin: `${theme.spacing(2)}px 0`,
  },
  label: {
    marginBottom: theme.spacing(1),
    position: 'relative',
    transform: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  studioRate: {
    color: theme.palette.secondary.light,
    display: 'inline-block',
    direction: 'rtl;',

    '& input[type=radio]': {
      display: 'none',
    },

    '& label': {
      display: 'inline-block',
    },
    '& label:hover': {
      color: theme.palette.primary.main,
    },
    '& label:hover  ~ label': {
      color: theme.palette.primary.main,
    },
    '& input[type=radio]:checked+label': {
      color: theme.palette.primary.main,
    },
    '& input[type=radio]:checked+label  ~ label': {
      color: theme.palette.primary.main,
    },
    '& svg': {
      display: 'inline-block',
      fontSize: '1.4em',
    },
  },
  flex: {}, // Added missing flex class
});

export default withStyles(styles)(
  connect(mapStateToProps)(
    withFormik<ContactStudioFormProps, FormValues>({
      mapPropsToValues: (props) => {
        return {
          domain: props.domain.domain.id,
          anonymous: false,
          studioId: props.studio._id,
          ownerId: props.studio.owner_id,
        };
      },
      // Custom validation
      validate: (values) => {
        const errors: { [key: string]: string } = {};
        const mandatoryFields = ['canSubmit', 'rank'];

        mandatoryFields.forEach((field) => {
          if (!values[field as keyof FormValues]) {
            errors[field] = '*';
          }
        });

        return errors;
      },
      handleSubmit: (values, { setSubmitting, resetForm, props }) => {
        setSubmitting(true);
        const api = serviceLocator.reviewApi;
        api.addClientReview(values).then((data) => {
          setSubmitting(false);
          props.onSuccess();
        }, LayoutHelper.onErr);
      },
      displayName: 'ContactStudioForm',
    })(ContactStudioForm)
  )
);
